<template>
  <section class="c2c-login">
    <div class="c2c-login-hero">
      <div class="c2c-login-logo" aria-hidden="true">
        <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
          <rect width="64" height="64" rx="14" fill="#3366cc"/>
          <text x="32" y="40" font-family="-apple-system, BlinkMacSystemFont, sans-serif" font-size="20" font-weight="800" fill="white" text-anchor="middle" letter-spacing="-0.5">c2c</text>
        </svg>
      </div>
      <h1>{{ t('app.title') }}</h1>
      <p class="c2c-login-tagline">
        {{ t('login.tagline_line1') }}<br>
        {{ t('login.tagline_line2') }}
      </p>
    </div>
    <div class="c2c-login-card">
      <h2>{{ t('login.card_heading') }}</h2>
      <p class="c2c-login-info">
        <i18n-t keypath="login.card_info" tag="span">
          <template #site><strong>meta.wikimedia.org</strong></template>
        </i18n-t>
      </p>
      <div class="c2c-login-action">
        <CdxButton action="progressive" weight="primary" @click="onLogin" :disabled="loading" size="large">
          {{ loading ? t('login.redirecting') : t('login.sign_in_button') }}
          <ExternalLink :size="20" />
        </CdxButton>
      </div>
      <div class="c2c-login-remember">
        <CdxCheckbox v-model="remember">{{ t('login.remember_me') }}</CdxCheckbox>
        <p class="c2c-login-remember-hint">{{ t('login.remember_hint') }}</p>
      </div>
      <p class="c2c-login-note">{{ t('login.first_time_note') }}</p>
    </div>

    <!-- Feature #8: try-before-you-login demo -->
    <DemoPreview @login="onLogin" />

    <section class="c2c-login-features">
      <h2>{{ t('login.how_heading') }}</h2>
      <ol class="c2c-feature-steps">
        <li>
          <span class="c2c-feature-num">1</span>
          <div>
            <h3>{{ t('login.step1_title') }}</h3>
            <p>{{ t('login.step1_desc') }}</p>
          </div>
        </li>
        <li>
          <span class="c2c-feature-num">2</span>
          <div>
            <h3>{{ t('login.step2_title') }}</h3>
            <p>{{ t('login.step2_desc') }}</p>
          </div>
        </li>
        <li>
          <span class="c2c-feature-num">3</span>
          <div>
            <h3>{{ t('login.step3_title') }}</h3>
            <p>{{ t('login.step3_desc') }}</p>
          </div>
        </li>
      </ol>
    </section>

    <section class="c2c-login-features">
      <h2>{{ t('login.features_heading') }}</h2>
      <ul class="c2c-feature-grid">
        <li>
          <strong>{{ t('login.feature_browser_title') }}</strong>
          <p>{{ t('login.feature_browser_desc') }}</p>
        </li>
        <li>
          <strong>{{ t('login.feature_codecs_title') }}</strong>
          <p>{{ t('login.feature_codecs_desc') }}</p>
        </li>
        <li>
          <strong>{{ t('login.feature_resolution_title') }}</strong>
          <p>{{ t('login.feature_resolution_desc') }}</p>
        </li>
        <li>
          <strong>{{ t('login.feature_categories_title') }}</strong>
          <p>{{ t('login.feature_categories_desc') }}</p>
        </li>
        <li>
          <strong>{{ t('login.feature_filename_title') }}</strong>
          <p>{{ t('login.feature_filename_desc') }}</p>
        </li>
        <li>
          <strong>{{ t('login.feature_cancel_title') }}</strong>
          <p>{{ t('login.feature_cancel_desc') }}</p>
        </li>
      </ul>
    </section>

    <section class="c2c-login-compare">
      <h2>{{ t('login.compare_heading') }}</h2>
      <div class="c2c-compare-table">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>{{ t('login.compare_col_v2c') }}</th>
              <th>{{ t('login.compare_col_c2c') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>{{ t('login.compare_row_where') }}</td><td>{{ t('login.compare_where_v2c') }}</td><td>{{ t('login.compare_where_c2c') }}</td></tr>
            <tr><td>{{ t('login.compare_row_outage') }}</td><td>{{ t('login.compare_yes') }}</td><td>{{ t('login.compare_no') }}</td></tr>
            <tr><td>{{ t('login.compare_row_youtube') }}</td><td>{{ t('login.compare_yes') }}</td><td>{{ t('login.compare_youtube_c2c') }}</td></tr>
            <tr><td>{{ t('login.compare_row_hevc') }}</td><td>{{ t('login.compare_hevc_v2c') }}</td><td>{{ t('login.compare_yes') }}</td></tr>
            <tr><td>{{ t('login.compare_row_queue') }}</td><td>{{ t('login.compare_queue_v2c') }}</td><td>{{ t('login.compare_queue_c2c') }}</td></tr>
            <tr><td>{{ t('login.compare_row_max') }}</td><td>{{ commonsMaxLabel }}</td><td>{{ ourMaxLabel }}</td></tr>
          </tbody>
        </table>
      </div>
      <p class="c2c-compare-note">
        <i18n-t keypath="login.compare_note" tag="span">
          <template #v2cLink>
            <a href="https://commons.wikimedia.org/wiki/Commons:Video2commons" target="_blank" rel="noopener noreferrer">video2commons</a>
          </template>
        </i18n-t>
      </p>
    </section>

    <!-- Visible FAQ. Same Q&As as the JSON-LD in index.html, but rendered
         in the page so users (not just search-engine bots) actually see
         them. <details> elements are native, accessible, and don't need
         JS - open them with mouse, keyboard, or screen reader. -->
    <section class="c2c-faq" :aria-label="t('faq.heading')">
      <h2>{{ t('faq.heading') }}</h2>
      <details>
        <summary>{{ t('faq.q_how_upload') }}</summary>
        <p>{{ t('faq.a_how_upload') }}</p>
      </details>
      <details>
        <summary>{{ t('faq.q_vs_v2c') }}</summary>
        <p>{{ t('faq.a_vs_v2c') }}</p>
      </details>
      <details>
        <summary>{{ t('faq.q_formats') }}</summary>
        <p>{{ t('faq.a_formats') }}</p>
      </details>
      <details>
        <summary>{{ t('faq.q_free') }}</summary>
        <p>{{ t('faq.a_free') }}</p>
      </details>
      <details>
        <summary>{{ t('faq.q_account') }}</summary>
        <p>{{ t('faq.a_account') }}</p>
      </details>
    </section>
  </section>

  <AppDialog
    :visible="!!errorDialog"
    :title="t('login.dialog_error_title')"
    :message="errorDialog || ''"
    tone="error"
    :confirm-label="t('common.ok')"
    @confirm="errorDialog = null"
    @cancel="errorDialog = null"
  />
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref } from "vue"
import { ExternalLink } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { CdxButton, CdxCheckbox } from "@wikimedia/codex"
import { beginLogin } from "@/lib/oauth"
import { HARD_MAX_FILE_SIZE_BYTES, formatSizeLimit } from "@/lib/config"
// DemoPreview is lazy-loaded - it carries video markup and supporting
// state that the user doesn't need until the hero is visible. Splitting
// it out lets the home page paint faster on slow networks.
const DemoPreview = defineAsyncComponent(() => import("@/components/DemoPreview.vue"))
import AppDialog from "@/components/AppDialog.vue"

const { t } = useI18n()

/**
 * Read from the constant rather than typed into the table.
 *
 * These two cells were literal text, so when the hard cap moved to 1 GB the
 * comparison table carried on advertising 500 MB. Commons' own ceiling comes
 * from its API (siteinfo maxuploadsize), which reports 5 GB.
 */
const ourMaxLabel = formatSizeLimit(HARD_MAX_FILE_SIZE_BYTES)
const commonsMaxLabel = "5 GB"
const loading = ref(false)
const errorDialog = ref<string | null>(null)
/** Opt-in, default off. On, the refresh token is mirrored so a later visit
 *  signs in with no clicks; off keeps today's behaviour, where the session
 *  ends with the tab. */
const remember = ref(false)

async function onLogin() {
  loading.value = true
  try {
    await beginLogin(remember.value)
    // beginLogin redirects, so we never reach here unless something went wrong
    // before the redirect (e.g., crypto.subtle unavailable in an insecure
    // context). Hold the loading state in that case so the failure shows.
  } catch (err) {
    loading.value = false
    errorDialog.value = err instanceof Error ? err.message : "Login failed to start"
  }
}
</script>

<style scoped>
.c2c-login { display: flex; flex-direction: column; gap: 32px; }

.c2c-login-hero { text-align: center; padding: 24px 0 8px 0; }
.c2c-login-logo {
  display: inline-block; margin-bottom: 12px;
  filter: drop-shadow(0 4px 12px rgba(51, 102, 204, 0.25));
}
.c2c-login-hero h1 {
  margin: 0 0 8px 0; font-size: 32px; font-weight: 700; letter-spacing: -0.5px;
}
.c2c-login-tagline { color: #54595d; margin: 0; font-size: 15px; line-height: 1.6; }

.c2c-login-card {
  background: #f8f9fa; border: 1px solid #eaecf0;
  border-radius: 8px; padding: 24px; text-align: center;
}
.c2c-login-card h2 { margin: 0 0 12px 0; font-size: 20px; }
.c2c-login-info {
  color: #54595d; font-size: 14px; line-height: 1.6;
  margin: 0 auto 20px auto; max-width: 480px;
}
.c2c-login-action { display: flex; justify-content: center; margin-bottom: 12px; }
.c2c-login-note { color: #72777d; font-size: 12px; margin: 0 auto; max-width: 480px; }
.c2c-login-remember {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  margin: 14px auto 10px auto; max-width: 480px;
}
.c2c-login-remember-hint {
  margin: 0; font-size: 12px; color: var(--color-subtle, #54595d);
}

.c2c-login-features { padding: 16px 0; }
.c2c-login-features h2 { font-size: 18px; margin: 0 0 16px 0; }

.c2c-feature-steps {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 16px;
}
.c2c-feature-steps li { display: flex; gap: 16px; align-items: flex-start; }
.c2c-feature-num {
  width: 32px; height: 32px; flex-shrink: 0; border-radius: 50%;
  background: #eaf3ff; color: #2a4b8d;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 14px;
}
.c2c-feature-steps h3 { margin: 0 0 4px 0; font-size: 15px; }
.c2c-feature-steps p { margin: 0; color: #54595d; font-size: 13px; line-height: 1.5; }

.c2c-feature-grid {
  list-style: none; margin: 0; padding: 0;
  display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px;
}
.c2c-feature-grid li {
  background: white; border: 1px solid #eaecf0;
  border-radius: 4px; padding: 12px 14px;
}
.c2c-feature-grid strong { display: block; margin-bottom: 4px; font-size: 14px; }
.c2c-feature-grid p { margin: 0; font-size: 12px; color: #54595d; line-height: 1.5; }

.c2c-login-compare h2 { font-size: 18px; margin: 0 0 12px 0; }
.c2c-compare-table { overflow-x: auto; }
.c2c-compare-table table { width: 100%; border-collapse: collapse; font-size: 13px; }
.c2c-compare-table th, .c2c-compare-table td {
  text-align: start; padding: 8px 12px; border-bottom: 1px solid #eaecf0;
}
.c2c-compare-table th { background: #f8f9fa; font-weight: 600; }
.c2c-compare-table td:first-child { color: #54595d; }
.c2c-compare-note { margin: 12px 0 0 0; font-size: 12px; color: #72777d; }
.c2c-compare-note a { color: #3366cc; }

@media (max-width: 540px) {
  .c2c-login-hero h1 { font-size: 26px; }
  .c2c-login-card { padding: 16px; }
  .c2c-feature-grid { grid-template-columns: 1fr; }
}

/* FAQ section. Native <details>/<summary> for keyboard + screen-reader
   support. Sits below the comparison table. */
.c2c-faq {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #eaecf0;
}
.c2c-faq h2 { margin: 0 0 12px 0; font-size: 18px; color: #202122; }
.c2c-faq details {
  border: 1px solid #c8ccd1;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 8px;
  transition: border-color 0.15s;
}
.c2c-faq details[open] { border-color: #3366cc; }
.c2c-faq summary {
  padding: 12px 16px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: #202122;
  list-style: none;
  position: relative;
  padding-right: 36px;
  user-select: none;
  min-height: 44px;
  display: flex;
  align-items: center;
}
.c2c-faq summary::-webkit-details-marker { display: none; }
.c2c-faq summary::after {
  content: "+";
  position: absolute;
  right: 16px; top: 50%; transform: translateY(-50%);
  font-size: 18px; color: #3366cc; font-weight: 400;
}
.c2c-faq details[open] summary::after { content: "−"; }
.c2c-faq summary:hover { background: #f8f9fa; }
.c2c-faq details p {
  margin: 0;
  padding: 0 16px 14px 16px;
  font-size: 13px;
  line-height: 1.55;
  color: #54595d;
}
</style>
